// import logger from 'ws-scripts/modules/logger';
// import getEnv from '../getEnv';
// import mergeObjects from '../mergeObjects';
import cloneObject from '../cloneObject.js';
import linkTypeSelectors from './linkTypeSelectors';
import locationSelectors from './locationSelectors';

export const project = 'shiftnative';

const INVENTORY_TYPE_MAPPING = {
	NEW: 'New',
	USED: 'Used',
	CERTIFIED: 'CPO',
	ALL: 'Mixed',
	DEFAULT: ''
};

const buyNowOrCarBravoAttributes = {
	GMBUYSRP: {
		param: 'gmbuysrp',
		location: 'SRP',
		linkDestination: 'GMBuy'
	},
	GMBUYVDP: {
		param: 'gmbuyvdp',
		location: 'VDP',
		linkDestination: 'GMBuy'
	},
	GMBUYNAV: {
		param: 'gmbuynav',
		location: 'Global Nav',
		linkDestination: 'GMBuy'
	},
	CARBRAVOSRP: {
		param: 'carbravosrp',
		location: 'SRP',
		linkDestination: 'CarBravo'
	},
	CARBRAVOVDP: {
		param: 'carbravovdp',
		location: 'VDP',
		linkDestination: 'CarBravo'
	},
	CARBRAVONAV: {
		param: 'carbravonav',
		location: 'Global Nav',
		linkDestination: 'CarBravo'
	}
};

const getFormFieldName = (fieldName) => {
	let formFieldName = 'other';

	if (fieldName) {
		const fieldNameFormatted = fieldName.toLowerCase();

		if (fieldNameFormatted.includes('firstname')) {
			formFieldName = 'firstname';
		} else if (fieldNameFormatted.includes('lastname')) {
			formFieldName = 'lastname';
		} else if (fieldNameFormatted.includes('middlename')) {
			formFieldName = 'middlename';
		} else if (fieldNameFormatted.includes('phone')) {
			formFieldName = 'phonenumber';
		} else if (fieldNameFormatted.includes('email')) {
			formFieldName = 'emailaddress';
		} else if (fieldNameFormatted.includes('street')) {
			formFieldName = 'address';
		} else if (fieldNameFormatted.includes('city')) {
			formFieldName = 'city';
		} else if (fieldNameFormatted.includes('state')) {
			formFieldName = 'state';
		} else if (fieldNameFormatted.includes('postalcode')) {
			formFieldName = 'zipcode';
		} else if (fieldNameFormatted.includes('comments')) {
			formFieldName = 'comments';
		}
	}

	return formFieldName;
};

export const getFormType = ({ trackingId, mapping }) => {
	return mapping?.form?.[trackingId] || mapping?.form?.DEFAULT || trackingId;
};

export const sending = (args) => {
	const { data, preview, _event, eventData: _eventData } = args;

	// Event Viewer
	const reportsForEventViewer = [];
	const reportsForNewRelic = [];

	if (!preview && Object.keys(data).length) {
		const { data: dataLayer, event: eventToSend } = data;
		const sendArgs = eventToSend ? ['send', eventToSend] : ['send'];

		window.sd('dataLayer', cloneObject(dataLayer));
		window.sd(...sendArgs);

		reportsForEventViewer.push({
			event: dataLayer?.events || eventToSend,
			payload: dataLayer || '',
			sender: 'window.sd'
		});

		reportsForNewRelic.push({
			clientEventName: dataLayer?.events || eventToSend,
			// linkType
			sender: 'window.sd'
		});
	}

	return {
		reportsForEventViewer,
		reportsForNewRelic
	};
};

export const formDisplayType = (isModal) => {
	return isModal ? 'modal' : 'in-page';
};

const mapInventoryType = ({ certified, compositeType, newOrUsed }) => {
	const compositeTypeUppercase = compositeType
		? compositeType?.toUpperCase?.()
		: 'DEFAULT';
	let inventoryType = INVENTORY_TYPE_MAPPING[compositeTypeUppercase];
	if (inventoryType === undefined || compositeTypeUppercase === 'DEFAULT') {
		if (
			newOrUsed === 'used' &&
			(certified === 'true' || certified === true)
		) {
			inventoryType = INVENTORY_TYPE_MAPPING.CERTIFIED; // Used Certified
		} else {
			inventoryType =
				INVENTORY_TYPE_MAPPING[(newOrUsed || 'DEFAULT').toUpperCase()]; // New or Used
		}
	}

	return inventoryType;
};

export const mapVehicle = ({ vehicle }) => {
	let vehicleMap = {};

	if (vehicle && Object.keys(vehicle).length) {
		const {
			inventoryType,
			modelYear,
			make,
			model,
			trim,
			engine,
			transmission,
			interiorColor,
			exteriorColor,
			vin,
			internetPrice,
			msrp,
			fuelType,
			bodyStyle,
			driveLine,
			certified,
			stockNumber,
			newOrUsed
		} = vehicle;

		vehicleMap = {
			status: mapInventoryType({
				certified,
				compositeType: inventoryType,
				newOrUsed
			}),
			year: modelYear,
			make,
			model,
			trim,
			engine,
			transmission,
			interiorColor,
			exteriorColor,
			vin,
			msrp,
			displayedPrice: internetPrice,
			fuelType,
			bodyStyle,
			driveTrain: driveLine,
			stockNumber
		};
	}

	return vehicleMap;
};

const getPageBrand = ({ franchises = [] }) => {
	let pageBrand = '';

	franchises?.sort?.()?.forEach?.((val, index, arr) => {
		if (index === arr.length - 1) {
			pageBrand += val.substring(0, 2);
		} else {
			pageBrand += `${val.substring(0, 2)}|`;
		}
	});

	return pageBrand;
};

export const hasInventoryWidget = ({
	isVlp = false,
	pageName,
	pageWidgets = [],
	vehicleResultCount
}) => {
	const legacyVLP =
		isVlp && pageWidgets.includes('v9.widgets.inventory-listing.default');

	return (
		(isVlp && !legacyVLP) ||
		(legacyVLP && ![null, ''].includes(vehicleResultCount)) ||
		pageWidgets?.includes?.('ws-inv-listing') ||
		pageName?.includes?.('NO_RESULTS')
	);
};

const mapPageAlias = ({
	isVlp = false,
	pageName,
	pageType = '',
	pageWidgets = [],
	trackingTags = {},
	vehicleResultCount
}) => {
	return (
		trackingTags?.pageType ||
		pageType ||
		(hasInventoryWidget({
			isVlp,
			pageName,
			pageWidgets,
			vehicleResultCount
		}) &&
			'Vehicle Listing') ||
		'Other'
	);
};

export const isFeaturedVehiclePage = ({ pageName, pageWidgets }) => {
	return (
		pageWidgets?.includes?.('v9.widgets.inventory-featured.default.v1') &&
		!pageName?.includes?.('PROMOTIONS_LISTING') &&
		!pageName?.includes?.('NO_RESULTS') &&
		pageName !== 'INDEX'
	);
};

export const buildDataForPageView = ({
	_eventData,
	mapping = {},
	mapping: {
		configuration: {
			dealerBac = '',
			dealerLDA = '',
			dealerTDA = '',
			pageBrand = '',
			retailerId = ''
		}
	} = {}
}) => {
	const {
		dealership: {
			city = '',
			dealershipName = '',
			postalCode = '',
			stateProvince = ''
		} = {},
		page: {
			attributes: { locale = '', vehicleResultCount } = {},
			pageInfo: {
				isShowroomOverview = false,
				isVdp = false,
				isVlp = false,
				pageName = '',
				pagePath = '',
				pageWidgets = []
			} = {},
			trackingTags = {}
		} = {},
		site: {
			design: { variationId = '' } = {},
			franchises = [],
			siteInfo: { tier = '' } = {}
		} = {},
		user: { userCity, userPostalCode, userState } = {},
		vehicles = []
	} = window.DDC.dataLayer;

	let pageType;

	// custom pageType for feature vehicle pages for Toyota (US687839)
	if (
		isFeaturedVehiclePage({
			pageName,
			pageWidgets
		})
	) {
		pageType = 'Vehicle Listing';
	} else if (isShowroomOverview) {
		pageType = 'Model Showroom';
	} else if (
		franchises?.includes?.('subaru') &&
		pagePath === '/financing/index.htm'
	) {
		pageType = 'Finance';
	} else {
		pageType = mapPageAlias({
			franchises,
			isVlp,
			pageName,
			pagePath,
			pageType: mapping.pagealias.pageType,
			pageWidgets,
			trackingTags,
			vehicleResultCount
		});
	}

	const data = {
		...(dealerBac && { dealerBac: `[[dealerCode.${retailerId}]]` }),
		dealerCity: city,
		...(dealerLDA && { dealerLDA: `[[dealerCode.${dealerLDA}]]` }),
		dealerName: dealershipName,
		dealerState: stateProvince,
		...(dealerTDA && {
			dealerTDA: `[[dealerCode.${dealerTDA}]]`
		}),
		dealerZipCode: postalCode,
		language:
			locale.indexOf('_') > -1
				? locale.substr(0, locale.indexOf('_'))
				: locale,
		pageBrand:
			pageBrand ||
			getPageBrand({
				franchises
			}),
		pageType,
		sessionId: window.sessionStorage.getItem('userSessionId'),
		siteTechnologyVersion: variationId,
		userCity,
		userState,
		userZipCode: userPostalCode,
		websiteTier: tier.replace('tier', 'Tier ')
	};

	if (isVdp) {
		data.vehicleDetails = mapVehicle({
			vehicle: vehicles[0]
		});
	}

	if (pageName.indexOf('404') > -1) {
		data.errorCode = '404';
	}

	return data;
};

export const findPropertiesFromSelector = (mapping, element) => {
	let match;
	if (element instanceof Element) {
		match = mapping.find(({ selector }) => {
			let properties;
			if (typeof selector === 'string') {
				properties = element.matches(selector);
			} else if (selector instanceof Array) {
				properties = selector.find((sel) => element.matches(sel));
			}
			return properties;
		});
	}
	return match;
};

export const getBuyNowOrCarBravoAttributes = (eventData) => {
	const attributes = Object.values(buyNowOrCarBravoAttributes).find(
		(buyNowOrCarBravoAttribute) =>
			eventData?.url?.includes(buyNowOrCarBravoAttribute.param)
	);

	if (
		!attributes &&
		eventData?.attributes?.['data-navigation-id'] &&
		eventData?.url?.includes('carbravo')
	) {
		return buyNowOrCarBravoAttributes.CARBRAVONAV;
	}

	return attributes;
};

export const linkGeneric = ({
	element,
	eventData,
	groupingLinkTypeSelectors = [],
	groupingLocationSelectors = []
}) => {
	const { widgetName } = eventData;

	let shouldTrack = true;
	let data;

	if (
		widgetName === 'ws-inv-listing' &&
		eventData?.attributes?.class?.includes?.('window-sticker-link')
	) {
		shouldTrack = false;
	}

	if (shouldTrack) {
		const { isTelephonicUrl } = eventData;
		if (
			element?.matches?.(
				'.navbar .nav-directions-button a, .page-header .adr a'
			) &&
			eventData.url.includes('directions')
		) {
			data = {
				events: 'getDirections'
			};
		} else if (isTelephonicUrl) {
			const { department, departmentId } = eventData;
			const approvedDepartments = ['Sales', 'Service', 'Parts'];
			const idPhoneMap = {
				SALES: 'Sales',
				SERVICE: 'Service',
				PARTS: 'Parts'
			};

			// Sometimes the id is listed in the department variable, which makes it in all caps
			const formattedDepartment = idPhoneMap[department]
				? idPhoneMap[department]
				: department;

			const correctDepartment =
				formattedDepartment &&
				approvedDepartments.includes(formattedDepartment)
					? formattedDepartment
					: idPhoneMap[departmentId];

			data = {
				clickToCallDepartment: correctDepartment || 'Other',
				events: 'clickToCall'
			};
		} else {
			const {
				attributes: {
					'data-src': dataSrc,
					'data-link-type': dataLinkType
				} = {},
				text,
				url
			} = eventData;

			let { linkType } = eventData;

			const { events, type } =
				findPropertiesFromSelector(
					[...groupingLinkTypeSelectors, ...linkTypeSelectors],
					element
				) || {};

			if (!linkType && type) {
				linkType = type;
			}

			const linkEvents = events || 'linkClick';

			linkType = dataLinkType || linkType || 'CTA';

			const { location } = findPropertiesFromSelector(
				[...groupingLocationSelectors, ...locationSelectors],
				element
			) || {
				location: eventData.location
			};

			let vehicleDetails;

			const { isVdp, isVlp } = window.DDC.dataLayer.page.pageInfo;

			if (
				(isVdp || isVlp) &&
				(url || dataSrc)?.includes('api/legacy/pse/windowsticker')
			) {
				const { status, year, make, model, vin } = mapVehicle({
					vehicle:
						eventData?.vehicle ||
						(isVdp && window.DDC.dataLayer.vehicles[0])
				});

				linkType = 'Monroney Label';

				vehicleDetails = {
					status,
					year,
					make,
					model,
					vin
				};
			}

			let locationToSend = location;
			if (location === 'mainNav') {
				locationToSend =
					eventData.trackingSection === 'Footer'
						? 'footer'
						: 'global nav';
			}

			if (linkType) {
				const { pageType } =
					window.DDC.WidgetData['ws-tagging'].mapping.shiftnative;
				data = {
					events: linkEvents,
					...(linkType && { linkType }),
					...(locationToSend && { location: locationToSend }),
					...(pageType && { pageType }),
					...(vehicleDetails && { vehicleDetails }),
					...(['CTA', 'Monroney Label'].includes(linkType) && {
						linkText: text
					})
				};
			}

			const { location: locationBuyNowOrCarBravo, linkDestination } =
				getBuyNowOrCarBravoAttributes(eventData) || {};
			if (locationBuyNowOrCarBravo) {
				data.location = locationBuyNowOrCarBravo;
				data.linkDestination = linkDestination;
			}
		}
	}

	return {
		data
	};
};

export const handleFormChange = ({ eventData, mapping }) => {
	const data = {
		displayType: formDisplayType(eventData.isModal),
		events: 'formFieldInteraction',
		formFieldName: getFormFieldName(eventData.fieldName),
		formType: getFormType({
			trackingId: eventData.trackingId,
			mapping
		}),
		formVehicle: mapVehicle({
			vehicle: eventData.vehicle
		})
	};

	return {
		data
	};
};

export const getInventoryType = () => {
	const inventoryTypeMapping = {
		NEW: 'New',
		USED: 'Used',
		CERTIFIED: 'CPO',
		ALL: 'Mixed',
		DEFAULT: ''
	};
	let inventoryType = inventoryTypeMapping.DEFAULT;
	const { vehicles } = window.DDC.dataLayer;

	if (vehicles.length) {
		const hasNew = vehicles.some((car) => car.newOrUsed === 'new');
		const hasUsed = vehicles.some((car) => car.newOrUsed === 'used');
		const hasCertified = vehicles.some(
			(car) => car.certified === 'true' || car.certified === true
		);
		const allCertified = !vehicles.some(
			(car) => car.certified === 'false' || car.certified === false
		);

		if (hasNew && hasUsed) {
			inventoryType = inventoryTypeMapping.ALL;
		} else if (hasNew) {
			inventoryType = inventoryTypeMapping.NEW;
			// note the next blocks have all vehicles used
		} else if (allCertified) {
			inventoryType = inventoryTypeMapping.CERTIFIED;
		} else if (hasCertified) {
			inventoryType = inventoryTypeMapping.ALL;
		} else {
			inventoryType = inventoryTypeMapping.USED;
		}
	}
	return inventoryType;
};

export const setFilterValueToSend = (data, delimiter) => {
	const modifiedData = !data ? 'All' : data;
	return modifiedData.replace
		? modifiedData.replace(/,/g, delimiter)
		: modifiedData;
};
